import { useState } from "react";

import { Checkbox, Combobox } from "@newsela/angelou";
import { isEmpty, isNil } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import GRADES from "@/constants/GRADES";
import { subjectForSlug } from "@/subjects/subjects.constants";
import useUser from "@/users/useUser";
import {
  checkboxCss,
  comboboxCss,
  containerCss,
  detailsContainerCss,
} from "./ClassroomSelectionBox.style";

const propTypes = {
  id: PropTypes.string,
  classroom: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    checked: PropTypes.bool,
    grade: PropTypes.number,
    subject_slugs: PropTypes.arrayOf(PropTypes.string).isRequired,
    is_synced: PropTypes.bool,
    school: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
  }).isRequired,
  defaultValue: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    grade: PropTypes.number,
    subject_slugs: PropTypes.arrayOf(PropTypes.string).isRequired,
    is_synced: PropTypes.bool,
    school: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
  }).isRequired,
  editables: PropTypes.shape({
    grade: PropTypes.bool,
    subject_slugs: PropTypes.bool,
    school: PropTypes.bool,
  }).isRequired,
  schools: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  ),
  onChange: PropTypes.func.isRequired,
  forceTouched: PropTypes.bool,
  qaSelectors: PropTypes.shape({
    gradeQASelector: PropTypes.string,
    subjectQASelector: PropTypes.string,
    schoolQASelector: PropTypes.string,
  }),
};

const GRADES_INDEX = [...Array(GRADES.length).keys()];

const ClassroomSelectionBox = ({
  classroom,
  defaultValue: {
    grade: initialGrade,
    subject_slugs: initialSubjectSlugs,
    school: initialSchool,
  },
  schools,
  editables,
  onChange,
  forceTouched,
  qaSelectors: {
    gradeQASelector = "classroom-selection-grade-level",
    subjectQASelector = "classroom-selection-subject",
    schoolQASelector = "classroom-selection-school",
  },
}) => {
  const {
    data: { teacher },
  } = useUser();
  const isChecked = classroom.checked || classroom.is_synced;
  const [gradeTouched, setGradeTouched] = useState(forceTouched);
  const [subjectTouched, setSubjectTouched] = useState(forceTouched);
  const [schoolTouched, setSchoolTouched] = useState(forceTouched);
  const resetTouched = () => {
    setGradeTouched(false);
    setSubjectTouched(false);
    setSchoolTouched(false);
  };

  const onClickCheckbox = () => {
    resetTouched();
    onChange({
      ...classroom,
      checked: !classroom.checked,
      school: isEmpty(classroom.school) ? teacher.school : classroom.school,
    });
  };

  return (
    <div css={containerCss}>
      <Checkbox
        label={classroom.name}
        __cssFor={checkboxCss}
        checked={isChecked}
        disabled={classroom.is_synced}
        onClick={onClickCheckbox}
      />
      {isChecked && (
        <div css={detailsContainerCss}>
          <Combobox
            label="Grade"
            placeholder="Select grade"
            selectedItem={classroom.grade ?? null}
            itemToString={(key) => GRADES[key] || ""}
            onSelectedItemChange={(e) =>
              onChange({ ...classroom, grade: e.selectedItem })
            }
            createInputProps={() => ({
              disabled:
                !editables.grade ||
                (!isNil(initialGrade) && classroom.is_synced),
              onBlur: () => setGradeTouched(true),
              "data-qa-selector": gradeQASelector,
            })}
            readonly={
              !editables.grade || (!isNil(initialGrade) && classroom.is_synced)
            }
            error={isNil(classroom.grade) ? "Select a grade" : ""}
            touched={gradeTouched || forceTouched}
            items={GRADES_INDEX}
            __cssFor={{
              root: comboboxCss,
            }}
          />
          <Combobox
            label="Subject"
            itemToString={(key) => subjectForSlug[key] || ""}
            items={Object.keys(subjectForSlug)}
            selectedItem={classroom.subject_slugs[0] ?? null}
            onSelectedItemChange={({ selectedItem }) =>
              onChange({
                ...classroom,
                subject_slugs: selectedItem ? [selectedItem] : [],
              })
            }
            createInputProps={() => ({
              disabled:
                !editables.subject_slugs ||
                (!isEmpty(initialSubjectSlugs) && classroom.is_synced),
              onBlur: () => setSubjectTouched(true),
              "data-qa-selector": subjectQASelector,
            })}
            readonly={
              !editables.subject_slugs ||
              (!isEmpty(initialSubjectSlugs) && classroom.is_synced)
            }
            error={isEmpty(classroom.subject_slugs) ? "Select a subject" : ""}
            touched={subjectTouched || forceTouched}
            __cssFor={{
              root: comboboxCss,
            }}
          />
          <Combobox
            label="School"
            items={schools}
            itemToString={(school) => school.name || ""}
            onSelectedItemChange={(e) =>
              onChange({ ...classroom, school: e.selectedItem || {} })
            }
            createInputProps={() => ({
              disabled:
                !editables.school ||
                (!isNil(initialSchool) && classroom.is_synced),
              onBlur: () => setSchoolTouched(true),
              "data-qa-selector": schoolQASelector,
            })}
            readonly={
              !editables.school ||
              (!isNil(initialSchool) && classroom.is_synced)
            }
            error={isEmpty(classroom.school) ? "Select a school" : ""}
            selectedItem={classroom.school ?? {}}
            touched={schoolTouched || forceTouched}
            __cssFor={{
              root: comboboxCss,
            }}
          />
        </div>
      )}
    </div>
  );
};

ClassroomSelectionBox.propTypes = propTypes;

export default ClassroomSelectionBox;
